import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { PageSection } from "@/components/site/PageSection";
import { SectionCta } from "@/components/site/SectionCta";
import { useHashFocus } from "@/lib/anchor-focus";

import { EsgBalloonCoverageTable } from "@/components/site/EsgBalloonCoverageTable";
import { faqSchema, medicalWebPageSchema, type Faq } from "@/components/site/InsuranceProse";
import { alternateLinks } from "@/lib/hreflang";

const CANONICAL = "https://virginiabariatric.com/resources/insurance-coverage";

const COVERAGE_IMAGE = {
  url: "https://virginiabariatric.com/og/insurance/og-insurance-coverage.png",
  alt: "Bariatric surgery insurance coverage guide for Virginia patients — Virginia Bariatric Surgery",
  width: 2400,
  height: 1260,
};

const COVERAGE_FAQS: Faq[] = [
  {
    q: "Does Anthem Blue Cross Blue Shield of Virginia cover bariatric surgery?",
    a: "Most Anthem BCBS of Virginia plans cover sleeve gastrectomy and gastric bypass. You must meet NIH criteria: a BMI of 40+, or 35+ with an obesity-related condition. You must also complete a 3- to 6-month physician-supervised weight-loss program. Some employer plans exclude bariatric surgery, so a benefits check is required.",
  },
  {
    q: "Does Medicare cover weight-loss surgery in Virginia?",
    a: "Yes. Medicare covers sleeve gastrectomy, gastric bypass, and duodenal switch. You need a BMI of 35 or higher and at least one obesity-related condition. Surgery must take place at a Medicare-approved facility. Medicare does not require a supervised diet.",
  },
  {
    q: "Does Aetna require a supervised diet?",
    a: "Yes, in most cases. Most Aetna commercial plans require 12 or more documented sessions in a behavioral weight-loss program. Standard BMI criteria and a psychological evaluation also apply.",
  },
  {
    q: "How long does bariatric surgery pre-authorization take?",
    a: "Most Virginia insurers approve within about two weeks once your packet is complete. Our office assembles the supervised-diet records, psychological and nutrition evaluations, and test results. Then we submit and track the request for you.",
  },
  {
    q: "What if my insurance denies bariatric surgery coverage?",
    a: "First-pass denials are common. We appeal on your behalf. Many denials resolve through a peer-to-peer call with the insurer's medical reviewer. If your plan excludes bariatric surgery entirely, you still have options. See what to do if you can't get approved.",
  },
  {
    q: "Does insurance cover endoscopic sleeve gastroplasty (ESG)?",
    a: "Increasingly, yes. ESG received a permanent CPT code (43889) effective January 1, 2026. Major insurers, including Cigna and Anthem, now cover it for qualifying patients. They use the same BMI criteria as sleeve gastrectomy. Aetna and UnitedHealthcare still consider ESG investigational. Medicare and Virginia Medicaid do not cover it, so we verify your individual benefits. Self-pay pricing and payment plan options are available.",
  },
  {
    q: "Does insurance cover the intragastric balloon?",
    a: "Usually not. The intragastric balloon is FDA-approved, but almost every plan treats it as a non-covered weight-management service. That includes Medicare and Virginia Medicaid. In practice, it is a self-pay procedure. We are glad to run a benefits check in case your plan is an exception.",
  },
];


export const Route = createFileRoute("/resources/insurance-coverage")({
  head: () => ({
    meta: [
      { title: "Bariatric Surgery Insurance Coverage Guide | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Virginia's morbid obesity mandate plus Anthem, Aetna, Cigna, United, Medicare, and Cardinal Care rules — BMI criteria, supervised diets, and ESG coverage.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Bariatric Surgery Insurance Coverage Guide | Virginia Bariatric" },
        { property: "og:description", content: "Virginia's morbid obesity mandate plus Anthem, Aetna, Cigna, United, Medicare, and Cardinal Care rules — BMI criteria, supervised diets, and ESG coverage." },
        { property: "og:type", content: "article" },
        { property: "og:url", content: CANONICAL },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-coverage.png" },
        { property: "og:image:alt", content: "Bariatric surgery insurance coverage guide for Virginia patients — Virginia Bariatric Surgery" },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:image", content: "https://virginiabariatric.com/og/insurance/og-insurance-coverage.png" },
        { name: "twitter:image:alt", content: "Bariatric surgery insurance coverage guide for Virginia patients — Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "Bariatric Surgery Insurance Coverage Guide | Virginia Bariatric" },
        { name: "twitter:description", content: "Virginia's morbid obesity mandate plus Anthem, Aetna, Cigna, United, Medicare, and Cardinal Care rules — BMI criteria, supervised diets, and ESG coverage." },
      ] : []),
    ],
    links: [{ rel: "canonical", href: CANONICAL }, ...alternateLinks("/resources/insurance-coverage")],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            medicalWebPageSchema({
              url: CANONICAL,
              name: "Bariatric Surgery Insurance Coverage in Virginia",
              description:
                "Plan-by-plan bariatric surgery insurance coverage guide for Virginia patients: Anthem BCBS, Aetna, Cigna, United, Medicare, and Medicaid.",
              image: COVERAGE_IMAGE,
              hasFaq: true,
              audienceType: "Patients evaluating insurance for bariatric surgery",
            }),
            faqSchema(COVERAGE_FAQS, CANONICAL),
          ],
        }),
      },
    ] : [],

  }),
  component: InsuranceCoveragePage,
});

function InsuranceCoveragePage() {
  useHashFocus();
  return (

    <>
      <PageSection className="pt-6 pb-3 text-center">
        <h1 className="text-4xl md:text-5xl font-serif text-brand-mid">
          Bariatric Surgery Insurance Coverage
        </h1>
        <p className="mt-2 text-lg text-muted-foreground italic">
          A plan-by-plan guide for Virginia patients
        </p>
        <div className="mt-4 flex justify-center">
          <Link
            to="/medical-weight-loss/insurance"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-white hover:opacity-90"
          >
            Looking for medication coverage instead?
          </Link>
        </div>
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-6">
        <article className="leading-[1.6] text-base md:text-lg space-y-3 [&_p]:mb-0 [&_h2]:pt-3 [&_h2]:mb-0">
          <p>
            Most major insurance plans in Virginia cover bariatric surgery. You just
            need to meet standard medical criteria. However, some employer contracts
            exclude bariatric surgery entirely. That is why we run a benefits check
            before every consultation. Here is what we see most often, plan by plan.
          </p>


          <h2 id="standard-criteria" className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Standard medical criteria
          </h2>
          <p>
            Almost every insurer starts from the same medical criteria. You qualify
            with a BMI of 40 or higher. You may also qualify with a BMI of 35 or higher
            plus at least one obesity-related condition. These conditions include type 2
            diabetes, high blood pressure (hypertension), sleep apnea, severe joint
            disease, and fatty liver disease.
            <a href="#fn1" id="fnref1" className="text-brand-mid align-super text-xs no-underline">1</a>{" "}
            See our full{" "}
            <Link to="/resources/qualifications" className="text-brand-mid underline">
              weight loss surgery eligibility criteria
            </Link>{" "}
            for the complete list.
          </p>

          <h2 id="virginia-mandate" className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Virginia&rsquo;s morbid obesity coverage mandate
          </h2>
          <p>
            Virginia law requires large-group employer health plans to cover treatment
            for morbid obesity (Code of Virginia § 38.2-3418.13). The law defines morbid
            obesity as a BMI of 40 or higher, or 35 or higher with a condition such as
            <Link to="/resources/obesity-and-diabetes" className="text-brand-mid underline">
              type 2 diabetes
            </Link>
            , hypertension, or sleep apnea. Plans must cover this care on
            terms comparable to other medical care.
          </p>
          <p>
            The mandate does not apply to individual and small-group plans. Coverage
            under those plans varies by policy. That is why we run a benefits check on
            your specific plan rather than relying on the payor&rsquo;s general policy.
          </p>


          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Anthem Blue Cross Blue Shield of Virginia
          </h2>
          <p>
            Anthem BCBS of Virginia and HealthKeepers (policy CG-SURG-83) cover{" "}
            <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery</Link>, Roux-en-Y gastric
            bypass, and revisional surgery under most plans. Typical requirements
            include:
          </p>

          <ul className="list-disc pl-6 space-y-0.5">
            <li>Age 18 or older</li>
            <li>Standard BMI criteria (40+, or 35+ with a qualifying condition)</li>
            <li>A documented weight-loss program — usually 3 to 6 monthly visits</li>
            <li>Medical clearance and a psychological evaluation</li>
            <li>A nutrition evaluation with a registered dietitian</li>
          </ul>
          <p>
            Anthem also covers ESG under the same criteria. It does not cover the
            intragastric balloon.
          </p>

          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Aetna
          </h2>
          <p>
            Aetna commercial plans (policy CPB 0157) typically cover sleeve gastrectomy
            and gastric bypass. You need a BMI of 40 or higher, or 35 or higher with
            severe comorbidities.
          </p>
          <p>
            Aetna is one of the stricter payors on documentation. It generally requires
            12 or more documented sessions in a behavioral weight-loss program. Those
            sessions must fall within the two years before surgery. A psychological
            evaluation is also required. Aetna currently considers ESG and the
            intragastric balloon investigational.
          </p>

          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Cigna
          </h2>
          <p>
            Cigna (policy 0051) is among the most progressive payors in Virginia. It
            covers sleeve gastrectomy, gastric bypass, and duodenal switch at a BMI of
            35 or higher — no other condition required. It also covers these procedures
            at a BMI of 30 to 34.9 with one obesity-related condition.
          </p>
          <p>
            Cigna covers ESG as medically necessary under the same criteria. Balloons
            are not covered.
          </p>

          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            UnitedHealthcare
          </h2>
          <p>
            UnitedHealthcare covers the standard bariatric procedures. You need a BMI of
            40 or higher, or 35 to 39.9 with at least one comorbidity. A preoperative
            evaluation and psychosocial assessment are also required.
          </p>
          <p>
            Supervised-diet requirements vary by employer plan. Some require 6 months.
            Others require none at all. That makes a benefits check essential.
            UnitedHealthcare currently considers ESG and balloons unproven.
          </p>


          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Medicare
          </h2>
          <p>
            Medicare (National Coverage Determination 100.1) covers sleeve gastrectomy,
            Roux-en-Y gastric bypass, duodenal switch, and adjustable gastric banding.
            You need a BMI of 35 or higher and at least one obesity-related condition.
            You also need documentation that medical treatment for obesity has been
            unsuccessful.
          </p>
          <p>
            Medicare does <em>not</em> require a supervised diet. That makes
            pre-authorization noticeably faster for Medicare patients. Surgery must take
            place at a Medicare-approved facility — and we are one. Medicare does not
            cover ESG or the intragastric balloon.
          </p>

          <h2 className="text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Virginia Medicaid
          </h2>
          <p>
            Virginia Medicaid (Cardinal Care) covers medically necessary bariatric
            surgery with prior authorization. Members enroll through managed care plans.
            These include Anthem HealthKeepers Plus, Aetna Better Health, Sentara
            Community Plan, and UnitedHealthcare Community Plan.
          </p>
          <p>
            Each plan runs its own medical-necessity review. Most require a BMI of 40 or
            higher, or 35 or higher with a serious obesity-related condition. You also
            need documentation that non-surgical treatment has failed. ESG and the
            balloon are generally not covered.
          </p>


          <h2 id="esg-balloon-coverage" className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Insurance coverage for ESG and the intragastric balloon
          </h2>
          <p>
            Coverage for{" "}
            <Link to="/services/esg" className="text-brand-mid underline">
              endoscopic sleeve gastroplasty (ESG)
            </Link>{" "}
            is improving quickly. ESG received a permanent CPT code (43889) effective
            January 1, 2026. Several major payors now cover it, including Cigna and
            Anthem. They apply the same BMI criteria used for sleeve gastrectomy.
          </p>
          <p>
            Aetna and UnitedHealthcare still classify ESG as investigational or
            unproven. Medicare and Virginia Medicaid do not cover it. That is why we
            verify your individual benefits before scheduling.
          </p>
          <p>
            The{" "}
            <Link to="/services/balloon" className="text-brand-mid underline">
              intragastric balloon
            </Link>{" "}
            is FDA-approved. Even so, almost every plan treats it as a non-covered
            weight-management service. That includes Anthem, Cigna, Aetna,
            UnitedHealthcare, Medicare, and Virginia Medicaid. In practice, it is a
            self-pay procedure.
          </p>

          <SectionCta to="/contact" label="cta_insurance_after_esg_balloon" search={{ topic: "insurance" }}>
            Request Free Benefits Check
          </SectionCta>

          <h3 className="text-xl font-serif text-brand-mid text-center">
            ESG and balloon coverage by payor
          </h3>
          <EsgBalloonCoverageTable />
          <p>
            Both procedures are still worth considering if you want a less invasive,
            non-surgical option. Bundled self-pay pricing is simple and transparent.
            Monthly{" "}
            <Link to="/resources/prosper" className="text-brand-mid underline">
              payment plans
            </Link>{" "}
            are also available.
          </p>





          <h2 id="self-pay-financing" className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center">
            Self-pay and financing
          </h2>
          <p>
            Does your plan exclude bariatric surgery? Or would you rather move faster
            than the insurance-mandated pre-surgery timeline? Self-pay is an
            alternative. We publish transparent bundled pricing &mdash; see our{" "}
            <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline">
              self-pay prices
            </Link>{" "}
            for every procedure. Learn{" "}
            <Link to="/resources/prosper" className="text-brand-mid underline">
              how to pay if your plan excludes surgery
            </Link>{" "}
            with medical financing and monthly payment plans.
          </p>


          <h2 id="pre-authorization" className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center">
            How we handle insurance pre-authorization
          </h2>
          <p>
            We verify your plan&rsquo;s bariatric surgery coverage at the very start. If
            your plan excludes coverage, you deserve to know before you invest time and
            energy in preparation.
          </p>
          <p>
            Once coverage or a payment plan is in place, we handle the paperwork. Our
            office tracks your supervised-diet visits. We gather your psychological and
            nutrition evaluations and any test results. Then we submit a complete
            pre-authorization packet on your behalf. Most approvals arrive within two
            weeks once the packet is complete.
            <a href="#fn2" id="fnref2" className="text-brand-mid align-super text-xs no-underline">2</a>
          </p>
          <p className="mt-6">
            Insurers sometimes deny requests on the first pass — often for unclear
            reasons. When that happens, we start the appeal for you. Many denials
            resolve through a peer-to-peer phone call with the insurer&rsquo;s medical
            staff. Others require the formal appeal process. Either way, we appeal on
            your behalf.
          </p>

        </article>

        <section aria-labelledby="insurance-faq" className="mt-8">
          <h2
            id="insurance-faq"
            className="scroll-mt-24 text-2xl md:text-3xl font-serif text-brand-mid text-center"
          >
            Bariatric Surgery Insurance FAQs
          </h2>
          <dl className="mt-4 space-y-5 leading-[1.6] text-base md:text-lg">
            <div id="does-anthem-blue-cross-blue-shield-of-virginia-cover-bariatr" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                Does Anthem Blue Cross Blue Shield of Virginia cover bariatric surgery?
              </dt>
              <dd className="mt-1">
                Most Anthem BCBS of Virginia plans cover sleeve gastrectomy and gastric
                bypass. You must meet NIH criteria: a BMI of 40+, or 35+ with an
                obesity-related condition. You must also complete a 3- to 6-month
                physician-supervised weight-loss program. Some employer plans exclude
                bariatric surgery, so a benefits check is required.
              </dd>
            </div>
            <div id="does-medicare-cover-weight-loss-surgery-in-virginia" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                Does Medicare cover weight-loss surgery in Virginia?
              </dt>
              <dd className="mt-1">
                Yes. Medicare covers sleeve gastrectomy, gastric bypass, and duodenal
                switch. You need a BMI of 35 or higher and at least one obesity-related
                condition. Surgery must take place at a Medicare-approved facility.
                Medicare does not require a supervised diet.
              </dd>
            </div>
            <div id="does-aetna-require-a-supervised-diet" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                Does Aetna require a supervised diet?
              </dt>
              <dd className="mt-1">
                Yes, in most cases. Most Aetna commercial plans require 12 or more
                documented sessions in a behavioral weight-loss program. Standard BMI
                criteria and a psychological evaluation also apply.
              </dd>
            </div>
            <div id="how-long-does-bariatric-surgery-pre-authorization-take" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                How long does bariatric surgery pre-authorization take?
              </dt>
              <dd className="mt-1">
                Most Virginia insurers approve within about two weeks once your packet
                is complete. Our office assembles the supervised-diet records,
                psychological and nutrition evaluations, and test results. Then we
                submit and track the request for you.
              </dd>
            </div>
            <div id="what-if-my-insurance-denies-bariatric-surgery-coverage" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                What if my insurance denies bariatric surgery coverage?
              </dt>
              <dd className="mt-1">
                First-pass denials are common. We appeal on your behalf. Many denials
                resolve through a peer-to-peer call with the insurer&rsquo;s medical
                reviewer. If your plan excludes bariatric surgery entirely, you still
                have options. See{" "}
                <Link
                  to="/resources/prosper"
                  hash="if-you-cant-get-approved"
                  className="text-brand-mid underline"
                >
                  what to do if you can&rsquo;t get approved
                </Link>.
              </dd>
            </div>
            <div id="does-insurance-cover-endoscopic-sleeve-gastroplasty-esg" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                Does insurance cover endoscopic sleeve gastroplasty (ESG)?
              </dt>
              <dd className="mt-1">
                Increasingly, yes.{" "}
                <Link to="/services/esg" className="text-brand-mid underline">
                  ESG
                </Link>{" "}
                received a permanent CPT code (43889) effective January 1, 2026. Major
                insurers, including Cigna and Anthem, now cover it for qualifying
                patients. They use the same BMI criteria as sleeve gastrectomy. Aetna
                and UnitedHealthcare still consider ESG investigational. Medicare and
                Virginia Medicaid do not cover it, so we verify your individual
                benefits. Self-pay pricing and{" "}
                <Link to="/resources/prosper" className="text-brand-mid underline">
                  payment plan options
                </Link>{" "}
                are available.
              </dd>

            </div>
            <div id="does-insurance-cover-the-intragastric-balloon" className="scroll-mt-24">
              <dt className="font-medium text-brand-mid">
                Does insurance cover the intragastric balloon?
              </dt>
              <dd className="mt-1">
                Usually not. The{" "}
                <Link to="/services/balloon" className="text-brand-mid underline">
                  intragastric balloon
                </Link>{" "}
                is FDA-approved, but almost every plan treats it as a non-covered
                weight-management service. That includes Medicare and Virginia
                Medicaid. In practice, it is a self-pay procedure. We are glad to run a
                benefits check in case your plan is an exception.
              </dd>

            </div>
          </dl>

        </section>


        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us to Verify Coverage
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Patient Links
          </Link>
        </div>

        <aside className="mt-8 pt-4 border-t border-border text-sm text-muted-foreground space-y-2 leading-snug">
          <p id="fn1">
            <sup>1</sup> Insurance companies operating in Virginia all use an outdated
            version of the NIH criteria for bariatric surgery eligibility that was
            published in 1991. The active NIH criteria have less restrictive BMI
            thresholds.{" "}
            <a href="#fnref1" className="text-brand-mid no-underline">↩</a>
          </p>
          <p id="fn2">
            <sup>2</sup> Virginia&rsquo;s health insurers claim that they are
            permitted two weeks to consider pre-authorization requests. The claim
            seems to conflict with the Code of Virginia, which states that health
            insurers are required to make prior authorization and coverage
            determinations within 5 calendar days for standard/non-urgent requests
            and 48 hours (or up to 24&ndash;72 hours depending on the specific health
            plan type or urgent drug protocols) for urgent requests. (See Code of
            Virginia, Title 38.2. Insurance &raquo; Chapter 34. Provisions Relating
            to Accident and Sickness Insurance &raquo; &sect; 38.2-3407.15:2.
            (Effective January 1, 2027) Carrier contracts; required provisions
            regarding prior authorization for drug benefits.){" "}
            <a href="#fnref2" className="text-brand-mid no-underline">↩</a>
          </p>
        </aside>
      <LastUpdated className="mt-10" />
      </PageSection>
    </>
  );
}

